// frontend/src/pages/DataHealth.jsx

import { useState, useEffect, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTabs } from '../tabs/useTabs';
import { AgGridReact } from 'ag-grid-react';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Collapse,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import PageBanner from '../components/common/PageBanner';
import { ICON, Archive, Barcode, CalendarBlank, ChartLineUp, CheckCircle, Clock, Coins, Copy, Cube, CurrencyDollar, EyeSlash, Handshake, Info, MapPin, MinusCircle, Package, Prohibit, TrendDown, Truck, WarningCircle } from '../icons';
import DisclosureCaret from '../components/common/DisclosureCaret';
import StatusChip from '../components/common/StatusChip';
import Tile from '../components/layout/Tile';
import {
  ToggleFixButtonCellRenderer,
  ActivateButtonCellRenderer,
  RemoveButtonCellRenderer,
  CreateInChannelButtonCellRenderer,
  LinkSupplierButtonCellRenderer,
  FixOrderRulesButtonCellRenderer,
  FixDosTargetCellRenderer,
  ErpLeftoverActionsCellRenderer,
} from '../components/datahealth/CellRenderers';
import DuplicateReview from '../components/datahealth/DuplicateReview';
import LinkSupplierToExactDialog from '../components/settings/LinkSupplierToExactDialog';
import { BATCH_EDITABLE_TYPES, issueDef } from '../datahealth/registry';
import {
  resolveIssue, buildItem, rowEndpointFor,
} from '../datahealth/resolveIssue';
import { hasStaleValues, reconcileRows } from '../datahealth/reconcile';
import { fixOptionsState, FIX_OPTIONS_STATE } from '../datahealth/fixOptions';
import { fetchAllPagesVia } from '../utils/fetchAllPages';
import CurrencyChangeConfirmModal from '../components/CurrencyChangeConfirmModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { useResolutionGate } from '../hooks/useResolutionGate';
import { useActiveErp } from '../hooks/useConnectors';
import { useFormatters } from '../hooks/useFormatters';
import { sessionApi } from '../services/SessionApiService';
import { ApiError, apiErrorFrom } from '../utils/parseApiError';
import { useAgGridTheme } from '../utils/agGridTheme';
import { channelName } from '../utils/channelName';
import { defaultColDef, defaultGridProps, editableGridProps, copyOnlyContextMenuItems, actionColumn, flexColumn } from '../utils/gridDefaults';
import { useIsShopify, itemIdentityColumns } from '../components/grid/productIdentity';
import { supplierCatalogPath } from './supplierCatalogLinks';
import { dosTargetFixPath } from '../datahealth/dosTargetFix';
import { useColumnTypes } from '../hooks/useColumnTypes';
// The category cell + its option source. What it OFFERS (taxonomy nodes, or
// the store's own product types plus a create row) is the store's capability
// to say — see useCategoryEditing below.
import {
  categoryPickerColDef, prefetchCategoryOptionsFirstPage,
} from '../utils/categoryPickerColDef';
import { categoryCellLabel, writesCategory } from '../utils/categoryEditing';
import { useGridState } from '../hooks/useGridState';
import { useDataHealthSummary } from '../hooks/useDataHealthData';
import { useDataHealthAction, useSetDataHealthIgnored } from '../hooks/useDataHealthMutations';
import { useEditableGridState } from '../hooks/useEditableGrid';
import { translateRequestError } from '../queries/api';
import { useQueryClient } from '@tanstack/react-query';
import { DATA, useOnDataChanged } from '../queries/dataChanged';
import { dataHealthSummary as dataHealthSummaryKey } from '../queries/keys';
import { useAuth, useCategoryEditing } from '../contexts/AuthContext';
import { isIgnoredVariantTitle } from '../utils/variantTitle';
import { referencePickerColDef, TYPEAHEAD_LIST_PARAMS } from '../utils/referencePickerColDef';
import { failedRowsMessage } from '../utils/failedRows';
import { showToast } from '../components/common/appToastBus';
import '../styles/pages.css';

const DEFAULT_PAGE_SIZE = 50;

// Floor for the "type the new value here" columns (`entryCol` below). Those
// columns are EMPTY on every row — the missing value is the gap being fixed —
// so auto-sizing, which sizes a column to its rendered content, has
// nothing to measure and collapses them to header width. Same reason
// gridDefaults' action columns carry ACTION_COL_MIN_WIDTH: a floor is the only
// thing autosize honours for a column it cannot measure
// (AG Grid clamps its measured width to colDef.minWidth).
export const ENTRY_COL_MIN_WIDTH = 140;

// The synthetic row key the category fix column edits. ONE key whatever the
// store writes: which server field it becomes is `categoryWrite`'s answer, so
// the row shape doesn't fork per store and neither does anything reading it.
export const CATEGORY_FIX_FIELD = '_new_category';

// ---------------------------------------------------------------------------
// Taxonomy — which issues exist, their category + display order, and whether
// each is "scored" (counts toward needs-attention) — is owned by the backend
// issue registry and arrives on the summary: `summary.issues[]` carries
// `{ key, category, scored, auto_resolved, ignored, count, ... }` and
// `summary.category_order` gives the section order. The bell badge counts the
// exact same scored set, so the two can't drift. The frontend keeps only
// presentation (icons / tone / action wiring) in ISSUE_DEFS below.
// ---------------------------------------------------------------------------

// ISSUE_DEFS (presentation + resolution wiring) and BATCH_EDITABLE_TYPES live
// in ./registry — extracted so the resolution taxonomy is unit-testable
// without loading this whole page. ICON_MAP / TONE_COLORS stay here (they need
// the Phosphor component imports).

// Map icon keys to Phosphor components
const ICON_MAP = {
  handshake: Handshake,
  clock: Clock,
  coins: Coins,
  currency: CurrencyDollar,
  chartLineUp: ChartLineUp,
  barcode: Barcode,
  calendar: CalendarBlank,
  eyeSlash: EyeSlash,
  package: Package,
  prohibit: Prohibit,
  truck: Truck,
  cube: Cube,
  minusCircle: MinusCircle,
  archive: Archive,
  trendDown: TrendDown,
  mapPin: MapPin,
  info: Info,
  warningCircle: WarningCircle,
  copy: Copy,
};

// Map tone to MUI color
const TONE_COLORS = {
  critical: 'error.main',
  warning: 'warning.main',
  info: 'info.main',
};


// Warms the picker's first page into the TanStack cache while the category
// issue grid is on screen. The in-cell Rich Select editor mounts only when the
// merchant starts editing; without this the first page (the ~10K-leaf taxonomy,
// or the store's own product types) is still in flight when it opens. Uses the
// same (storeId, term, page) key the editor's valuesPage reads, so the first
// open is instant. One warmer for either capability — the option endpoint is
// the same, and it is the SERVER that decides which population it serves.
function ProductCategoryPickerWarmer() {
  const queryClient = useQueryClient();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  useEffect(() => {
    prefetchCategoryOptionsFirstPage(queryClient, storeId);
  }, [queryClient, storeId]);
  return null;
}

// ============================================
// IssueGrid — AG Grid detail list per issue
// ============================================

function IssueGrid({ issueKey, entity, onItemFixed, onUnsavedEdits, ref }) {
  const { t } = useTranslation();
  const { openTab } = useTabs();
  const { selectedStore } = useAuth();
  // Setting a category only pushes to a sales channel on channel-connected
  // stores; on non-channel stores it's a local write, so the picker stays
  // editable there. (Shopify is the only channel today.)
  const isShopify = useIsShopify();
  const channel = channelName(selectedStore?.store_type, t);
  const queryClient = useQueryClient();
  const gridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const { formatNumber, parseNumber, formatCurrency } = useFormatters();
  const gridRef = useRef(null);
  // Per-issue grid state — sort/visibility/order is meaningful per tab
  // (each issue has different columns), so scope the saved state by issueKey.
  const gridState = useGridState(`data_health_${issueKey}`);

  const [rowData, setRowData] = useState(null);
  // Stable identity: getRowId is an AG Grid initial property, so a new
  // function on a later render cannot be applied (warning #22).
  const getRowId = useCallback(({ data }) => String(data.id), []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Bulk-edit dirty tracking via the unified primitives hook. The save flow
  // (handleSaveAll) stays custom because the endpoint and payload shape vary
  // per issueKey (datahealth/resolveIssue.js).
  const editing = useEditableGridState({ getRowId: (row) => row.id });
  const {
    onCellValueChanged, unsavedCellClass, isDirty,
    changedRows, clearByIds, clearAll, getChangedCellsRef,
  } = editing;

  const def = issueDef(issueKey);
  // What a category write MEANS on this store — which field it lands on,
  // whether the merchant may mint a value, how far it reaches. The category fix
  // column and `buildItem` both render from this rather than re-reading
  // `category_source` (core.category_editing is the one home for the rule).
  const categoryEditing = useCategoryEditing();

  // Options for the issues whose fix picks from a store-wide list
  // (`def.optionsUrl` — every location for missing_location, every supplier
  // for missing_supplier). Loading and failure are tracked separately from the
  // rows so `fixOptionsState` can tell "the store has none" from "the read
  // failed".
  //
  // Seeded from the def rather than reset in the effect: `optionsUrl` is
  // per-ISSUE and one IssueGrid renders one issue for its whole life, so an
  // issue with no picker is settled from the first render and never enters a
  // loading state it can't leave.
  const [options, setOptions] = useState(
    () => ({ isLoading: !!def?.optionsUrl, isError: false, rows: [] }),
  );
  const dropdownOptions = options.rows;
  const optionsState = fixOptionsState({ ...options, count: dropdownOptions.length });

  // Bound to issueKey at hook construction so the per-issue invalidation
  // set is correct for every action this grid surfaces (single-row +
  // batch save). Pending-state used as the batch-save spinner.
  const actionMutation = useDataHealthAction(issueKey);
  const batchSaving = actionMutation.isPending;

  // Inline cost-of-record confirmation. Assigning a default supplier
  // (missing_supplier) adopts that supplier's cost, which the backend gates
  // with `requires_confirmation`. The gate hosts CurrencyChangeConfirmModal
  // right here — no punt to Item Setup.
  const currencyGate = useResolutionGate();

  // The PO-impact gate, hosted in <ConfirmDialog> like every other
  // confirmation on this page; `confirm()` resolves true/false.
  const poGate = useConfirmDialog();
  const discardGate = useConfirmDialog();
  const confirmDiscard = discardGate.confirm;

  // supplier_not_linked_to_erp resolves via the per-row Link-to-Exact dialog
  // (bespoke connector action). The connector list is a shared cached query,
  // so reading it here dedupes with the Connectors page. `linkSupplier` holds the
  // row whose dialog is open (null = closed).
  const { connector: activeErp } = useActiveErp();
  const [linkSupplier, setLinkSupplier] = useState(null);
  const onLinkSupplier = useCallback((row) => setLinkSupplier(row), []);

  // contradictory_order_constraints resolves on the Supplier catalog screen (the
  // three order fields interact and are edited together there). Deep-link to
  // THIS item's default supplier's catalog, seeding the grid filter with the
  // item's code — supplierCatalogLinks owns the URL shape, including how a
  // code carrying '/' or '&' is spelled as one route segment.
  const onFixOrderRules = useCallback((row) => {
    if (!row?.supplier_id) {
      console.error('contradictory_order_constraints row missing supplier_id', row);
      return;
    }
    openTab(supplierCatalogPath(row.supplier_id, row.item_code));
  }, [openTab]);

  // dos_target_below_cycle resolves where the row's short target is set —
  // the item, its default supplier or the store (the row's `fix_level`);
  // dosTargetFix owns the destinations.
  const onFixDosTarget = useCallback((row) => {
    openTab(dosTargetFixPath(row));
  }, [openTab]);

  // erp_leftover_* Keep (adopt native) / Retire (soft-delete) — both POST to the
  // owning viewset's keep-native / retire action for the single row. On success
  // (updated > 0) the row leaves the issue; Retire warns about attached stock
  // first (see onRetireLeftover).
  const applyLeftover = useCallback(async (row, urlPath) => {
    try {
      const data = await actionMutation.mutateAsync({
        endpoint: `/api/${def.viewset}/${urlPath}/`,
        payload: { ids: [row.id] },
      });
      if (data?.updated > 0) {
        setRowData(prev => prev.filter(r => r.id !== row.id));
        setPagination(prev => prev ? { ...prev, total_items: prev.total_items - 1 } : prev);
        onItemFixed?.();
      } else {
        showToast(t('dataHealth.updateFailed'), 'error');
      }
    } catch (err) {
      console.error(`DataHealth ${urlPath} failed:`, err.message);
      showToast(translateRequestError(err, t, 'dataHealth.updateFailed'), 'error');
    }
  }, [def, actionMutation, onItemFixed, t]);

  const onKeepLeftover = useCallback((row) => applyLeftover(row, 'keep-native'), [applyLeftover]);

  const onRetireLeftover = useCallback((row) => {
    // Warn-but-allow: name the on-hand stock the soft-delete would hide.
    const onHand = row?.on_hand_total || 0;
    const message = onHand > 0
      ? t('dataHealth.retireConfirmStock', { count: onHand })
      : t('dataHealth.retireConfirm');
    if (!window.confirm(message)) return Promise.resolve();
    return applyLeftover(row, 'retire');
  }, [applyLeftover, t]);

  // EVERY option, not the first page of them. These lists are the set of fixes
  // the merchant is offered — a short one does not look short, it looks like
  // the store has no other location / supplier to choose, and the merchant
  // assigns the nearest wrong one. An over-long walk throws, which lands in
  // the same `isError` banner a transport failure does.
  useEffect(() => {
    if (!def?.optionsUrl) return;
    fetchAllPagesVia(sessionApi, def.optionsUrl)
      .then(rows => setOptions({ isLoading: false, isError: false, rows }))
      // Recorded, not swallowed: `isError` is what the banner below renders.
      // A silently empty list would read as a store with no locations.
      .catch(err => {
        console.error('DataHealth fix-options fetch failed:', err.message);
        setOptions({ isLoading: false, isError: true, rows: [] });
      });
  }, [def?.optionsUrl]);

  // missing_supplier needs only "does the store have ANY supplier?" (the picker
  // options are per-row, from each item's assigned suppliers) — a page_size=1
  // count, not the whole supplier list. Same three-state read as the options
  // above, and for the same reason: a failed count must not render as "you have
  // no suppliers", which would send the merchant to Supplier setup to create
  // one they already have.
  const [suppliers, setSuppliers] = useState(
    () => ({ isLoading: issueKey === 'missing_supplier', isError: false, count: 0 }),
  );
  useEffect(() => {
    if (issueKey !== 'missing_supplier') return;
    sessionApi.get('/api/suppliers/?page_size=1')
      .then(res => {
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        return res.json();
      })
      .then(data => setSuppliers({
        isLoading: false, isError: false,
        count: data.count,
      }))
      .catch(err => {
        console.error('DataHealth supplier count fetch failed:', err.message);
        setSuppliers({ isLoading: false, isError: true, count: 0 });
      });
  }, [issueKey]);
  const suppliersState = fixOptionsState(suppliers);
  const hasSuppliers = suppliersState === FIX_OPTIONS_STATE.READY;

  // One window of rows, addressed by KEYSET CURSOR — `null` means "from the
  // first row", which is also this grid's re-read.
  //
  // Not a page number: this grid splices resolved rows out of `rowData` after
  // every fix, and the population moves under it besides (a webhook or another
  // admin can add or clear rows). A page number addresses a POSITION, so each
  // row that leaves or arrives above the window shifts what that position
  // points at, and "Show more" then serves a row twice or never reaches it —
  // the merchant sees "N remaining" they cannot get to. A cursor addresses
  // content: "the rows after this one" is the same set however the population
  // moved. See `_detail_window` in backend/api/views/data_health.py.
  const fetchWindow = useCallback(async (cursor) => {
    const isFirst = cursor == null;
    if (isFirst) setLoading(true);
    else setLoadingMore(true);

    try {
      const params = new URLSearchParams({
        issue: issueKey, page_size: DEFAULT_PAGE_SIZE,
      });
      if (!isFirst) params.set('cursor', cursor);
      const res = await sessionApi.get(`/api/data-health/detail/?${params}`);
      if (!res.ok) throw await apiErrorFrom(res);
      const data = await res.json();
      if (isFirst) {
        setRowData(data.items);
        setError(null);
      } else {
        setRowData(prev => [...prev, ...data.items]);
      }
      setPagination(data.pagination);
    } catch (err) {
      console.error('DataHealth issue window fetch failed:', err.message);
      setError(translateRequestError(err, t, 'common.fetchError'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [issueKey, t]);

  // Initial load — once per mounted IssueGrid, whatever re-creates the effect
  // later, so an expanded issue keeps its accumulated windows. issueKey is
  // fixed per instance (one IssueGrid per issue card), so there is no
  // reload-on-key-change case; post-save re-reads go through handleSaveAll's
  // explicit fetchWindow(null), and the end of an import through the
  // store-data refresh below.
  const didInitialLoadRef = useRef(false);
  useEffect(() => {
    if (didInitialLoadRef.current) return;
    didInitialLoadRef.current = true;
    fetchWindow(null);
  }, [fetchWindow]);

  // The page keeps a card with unsaved edits in its list whatever the
  // summary's count reads (`DataHealth`, `issuesWithEdits`).
  useEffect(() => {
    onUnsavedEdits(issueKey, isDirty);
  }, [onUnsavedEdits, issueKey, isDirty]);

  // The card asks this before it closes the grid (`TaskCard`): resolves true
  // once the grid holds no unsaved edits — none to begin with, or the merchant
  // chose to discard them. Read off the dirty-cell map after committing a cell
  // still being typed in: that commit's state update has not rendered yet. The
  // discard clears the edits rather than leaving them to the unmount: the tab's
  // unsaved-edits flag is cleared from them, and `useDirtyTabReporter`
  // (tabs/useDirtyTabs) has no cleanup, so an unmount leaves it set.
  useImperativeHandle(ref, () => ({
    release: async () => {
      gridRef.current?.api?.stopEditing();
      if (getChangedCellsRef().size === 0) return true;
      const ok = await confirmDiscard({
        title: t('common.unsavedChangesTitle'),
        message: t('dataHealth.unsavedChangesClose'),
        confirmLabel: t('common.discard'),
        variant: 'warning',
      });
      if (ok) clearAll();
      return ok;
    },
  }), [getChangedCellsRef, clearAll, confirmDiscard, t]);

  // The whole store changed (`queries/dataChanged`, an import ended): the rows
  // live in this component, out of every cache, so read the first window
  // again. Over unsaved edits the read is owed instead — the new rows would
  // replace the ones the edits were typed into — and runs once the edits are
  // saved or discarded.
  const refreshOwedRef = useRef(false);
  useOnDataChanged([DATA.everything], () => {
    refreshOwedRef.current = isDirty;
    if (!isDirty) fetchWindow(null);
  });
  useEffect(() => {
    if (isDirty || !refreshOwedRef.current) return;
    refreshOwedRef.current = false;
    fetchWindow(null);
  }, [isDirty, fetchWindow]);

  // Single-row instant action (toggle / activate). Cell renderer passes
  // the row id; the page builds the bulk-update payload from the def
  // (fieldName + fixedValue) and dispatches to the owning viewset.
  const onAction = useCallback(async (rowId) => {
    if (!def?.viewset || def?.fieldName == null) {
      console.error('DataHealth onAction missing viewset/fieldName for', issueKey);
      showToast(t('dataHealth.updateFailed'), 'error');
      return;
    }
    const endpoint = `/api/${def.viewset}/bulk-update/`;
    // Tagged like every other Data Health write, so the response says whether
    // the row actually left the issue rather than only whether the write
    // landed. A toggle/activate resolves its issue, but nothing enforces that,
    // so the page reads the server's answer instead of inferring it.
    const payload = {
      data_health_issue: issueKey,
      items: [{ id: rowId, fields: { [def.fieldName]: def.fixedValue } }],
    };

    try {
      // Single-row actions only ever set a fixed toggle/activate value (never a
      // default-supplier election), so the cost-of-record gate can't fire here
      // — that lives on the batch (dropdown-edit) path in handleSaveAll. A
      // stray requires_confirmation would fall through to the generic error.
      const data = await actionMutation.mutateAsync({ endpoint, payload });
      const result = data?.results?.find(r => String(r.id) === String(rowId));
      if (!result?.success) {
        if (result?.error) console.error('DataHealth row action returned error:', result.error);
        showToast(t('dataHealth.updateFailed'), 'error');
        return;
      }
      if (!Array.isArray(data?.resolved_ids)) {
        // Endpoint dropped the contract — fail loud rather than silently
        // treating it as "nothing resolved" (a dead-looking button).
        console.error('DataHealth row action: response missing resolved_ids');
        showToast(t('dataHealth.updateFailed'), 'error');
        return;
      }

      // The same rule handleSaveAll applies, for a batch of one. It keeps the
      // functional setState form rather than calling `reconcileRows` over
      // `rowData`: reading the buffer here would put it in this callback's
      // deps, and the grid context would churn on every data change.
      const rowOutcome = {
        successIds: new Set([String(rowId)]),
        resolvedIds: new Set(data.resolved_ids.map(String)),
      };
      if (rowOutcome.resolvedIds.has(String(rowId))) {
        setRowData(prev => prev.filter(r => r.id !== rowId));
        setPagination(prev => prev ? { ...prev, total_items: prev.total_items - 1 } : prev);
        onItemFixed?.();
      } else if (hasStaleValues(rowOutcome)) {
        // Written but still offending: keep the row and re-read it so its
        // columns show what the server now holds.
        fetchWindow(null);
      }
    } catch (err) {
      console.error('DataHealth row action failed:', err.message);
      showToast(translateRequestError(err, t, 'dataHealth.updateFailed'), 'error');
    }
  }, [issueKey, def, t, onItemFixed, actionMutation, fetchWindow]);

  const removeRow = useCallback((id) => {
    setRowData(prev => prev.filter(r => r.id !== id));
    setPagination(prev => prev ? { ...prev, total_items: prev.total_items - 1 } : prev);
    onItemFixed?.();
  }, [onItemFixed]);

  // Per-row DELETE (stale_placement). The row id IS the ItemLocation pk, and
  // the endpoint comes from the write path (datahealth/resolveIssue.js).
  //
  // The guarded destroy's refusal — the placement still holds stock, an inbound
  // PO or an open transfer — gets its own sentence through the SAME mechanism
  // every other structured error on this page uses: `apiErrors.placement_in_use`
  // via translateRequestError.
  const onDelete = useCallback(async (rowId) => {
    try {
      await actionMutation.mutateAsync({
        endpoint: rowEndpointFor(issueKey, rowId), method: 'delete',
      });
      removeRow(rowId);
    } catch (err) {
      console.error('DataHealth row delete failed:', err.code, err.message);
      showToast(translateRequestError(err, t, 'dataHealth.updateFailed'), 'error');
    }
  }, [issueKey, actionMutation, removeRow, t]);

  // Per-row "create in sales channel" (erp_item_missing_channel_product).
  // Posts to the ItemViewSet create-in-channel action and returns the per-item
  // result to the cell renderer (which then shows the link to the new product).
  // The item is fixed on success (now has a channel product), so the badge
  // decrements; the row stays so its link remains visible until refresh.
  const onCreateInChannel = useCallback(async (rowId) => {
    try {
      const data = await actionMutation.mutateAsync({
        endpoint: '/api/items/create-in-channel/',
        payload: { item_ids: [rowId] },
      });
      const result = data?.results?.find(r => String(r.id) === String(rowId));
      if (result?.success) {
        // Persist the "created" state + link on the ROW (survives AG Grid cell
        // recycling on scroll — local cell state would not). The row stays so
        // the merchant can open the new product; it drops off on next refetch
        // once the item carries a channel GID.
        setRowData(prev => prev.map(r =>
          String(r.id) === String(rowId)
            ? { ...r, channel_created: true, channel_admin_url: result.admin_url }
            : r
        ));
        onItemFixed?.();
        // Tell the merchant plainly (not via a hover-only tooltip) that the
        // product exists but isn't published yet. The create itself succeeded,
        // so details the channel refused are a warning, not an error.
        const unpublished = t('dataHealth.channelProductsCreatedInfo', { channel });
        if (result.enrichment_failed) {
          showToast(`${t('dataHealth.createdWithEnrichmentWarning', { channel })} ${unpublished}`, 'warning');
        } else {
          showToast(unpublished, 'info');
        }
        return result;
      }
      if (result?.error) console.error('DataHealth create-in-channel error:', result.error);
      showToast(t('dataHealth.updateFailed'), 'error');
      return result || { success: false };
    } catch (err) {
      console.error('DataHealth create-in-channel failed:', err.message);
      showToast(translateRequestError(err, t, 'dataHealth.updateFailed'), 'error');
      return { success: false, error: err.message };
    }
  }, [actionMutation, onItemFixed, t, channel]);

  // `unsavedCellClass` is what tints an edited entry cell (defaultColDef's
  // ag-cell-unsaved rule reads it off the grid context).
  const gridContext = useMemo(() => ({
    onAction, onDelete, removeRow, onCreateInChannel, onLinkSupplier, onFixOrderRules,
    onFixDosTarget, onKeepLeftover, onRetireLeftover, channel, unsavedCellClass,
  }), [onAction, onDelete, removeRow, onCreateInChannel, onLinkSupplier, onFixOrderRules,
    onFixDosTarget, onKeepLeftover, onRetireLeftover, channel, unsavedCellClass]);

  const columnDefs = useMemo(() => {
    // Which entity a detail row is (supplier / location / item / item_location
    // / inventory) comes from the backend registry via the summary — no
    // per-issue set to keep in sync here, so a supplier issue can't fall
    // through to item columns.
    const priceParser = (params) => {
      if (params.newValue === '' || params.newValue === null || params.newValue === undefined) return null;
      const parsed = parseNumber(params.newValue);
      return isNaN(parsed) ? params.oldValue : parsed;
    };

    const itemValueGetter = ({ data }) => {
      if (!data) return '';
      const v = data.variant_title;
      const hasVariant = !isIgnoredVariantTitle(v);
      return `${data.product_title || data.item_name}${hasVariant ? ` — ${v}` : ''}`;
    };

    // Every "type/pick the new value here" column on this page. They share the
    // unsaved-cell highlight, and — because they hold no value until the
    // merchant enters one — they opt out of content measurement and rely on
    // ENTRY_COL_MIN_WIDTH instead: autosize would otherwise shrink them to
    // header width, and nothing widens them back.
    const entryCol = (colDef) => ({
      minWidth: ENTRY_COL_MIN_WIDTH,
      suppressAutoSize: true,
      editable: true,
      ...colDef,
    });

    // FK "pick a value to fix this row" column — same shared dropdown
    // builder (and therefore identical look/behaviour) as ItemSetup's
    // Supplier column, in flat-id mode since the save flow reads the raw
    // id straight off `row[field]`. `def.optionId`/`def.optionLabel` name
    // the id and label fields on each option from `dropdownOptions`.
    const dropdownEditCol = ({ field, headerName, width = 200 }) => entryCol({
      ...referencePickerColDef({
        field,
        options: dropdownOptions,
        optionId: def.optionId,
        formatLabel: (o) => o[def.optionLabel],
        flat: true,
      }),
      headerName,
      width,
    });

    // missing_supplier fix picker. Its options are PER-ROW because the two
    // populations resolve differently (see backend missing_supplier):
    //   * item HAS assigned suppliers, none default (the ERP no-MainSupplier
    //     case) → offer exactly those; the backend ELECTS the pick as default.
    //   * item has NONE (the native gap) → offer the whole store supplier list
    //     (`dropdownOptions`); the backend ASSIGNS the pick as the first relation.
    // Both write the raw id to `_new_supplier` (→ default_supplier_id); the item
    // wire routes elect-vs-assign, so a pick is always valid for its row.
    const supplierAssignPickerCol = () => {
      const poolFor = (data) => {
        const assigned = data?.assigned_suppliers || [];
        return assigned.length > 0 ? assigned : dropdownOptions;
      };
      const nameFor = (pool, id) => {
        const s = id != null && pool.find((o) => o.supplier_id === id);
        return s ? s.supplier_name : '—';
      };
      return entryCol({
        field: '_new_supplier',
        headerName: t('dataHealth.missing_supplierColHeader'),
        width: 220,
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: (params) => {
          const pool = poolFor(params.data);
          return {
            values: [null, ...pool.map((s) => s.supplier_id)],
            formatValue: (value) => nameFor(pool, value),
            ...TYPEAHEAD_LIST_PARAMS,
          };
        },
        valueGetter: (params) => params.data?._new_supplier ?? null,
        valueSetter: (params) => {
          const raw = params.newValue;
          if (raw == null || raw === '') {
            if ((params.data?._new_supplier ?? null) === null) return false;
            params.data._new_supplier = null;
            return true;
          }
          // Rich-select offers this row's pool, but fill-handle/paste bypass it:
          // resolve the incoming value against the pool by id OR name so both
          // paths work, and reject anything outside it (the backend would 400 an
          // unassigned pick on an item that already has relations).
          const match = poolFor(params.data).find(
            (s) => String(s.supplier_id) === String(raw) || s.supplier_name === String(raw));
          if (!match) return false;
          if (match.supplier_id === (params.data?._new_supplier ?? null)) return false;
          params.data._new_supplier = match.supplier_id;
          return true;
        },
        valueFormatter: (params) =>
          nameFor(poolFor(params.data), params.data?._new_supplier ?? null),
      });
    };

    // --- Supplier-entity issues (the connector-health ones: not-linked-to-ERP,
    // deleted-in-ERP). Both are linkage problems, so the row identifies the
    // supplier and the fix is an action, never an inline field edit. Email
    // helps the merchant tell two similarly-named suppliers apart. ---
    if (entity === 'supplier') {
      const cols = [
        { field: 'supplier_name', headerName: t('dataHealth.colSupplier'), ...flexColumn(1, 160) },
        { field: 'supplier_email', headerName: t('dataHealth.colEmail'), width: 250 },
      ];
      if (def.actionType === 'supplier-link') {
        // Per-row Link-to-Exact — opens the merge/adopt dialog for this
        // supplier (bespoke connector action, not a bulk-update).
        cols.push(actionColumn({
          colId: 'link_to_erp', headerName: t('dataHealth.colAction'),
          cellRenderer: LinkSupplierButtonCellRenderer,
        }));
      }
      return cols;
    }

    // --- Location-entity issues (the external connector-health leftovers) ---
    if (entity === 'location') {
      // Same rule as the item identity below: on a Shopify store the location
      // code is the location GID's numeric suffix, so only the name names the
      // row to the merchant.
      const cols = [
        ...(isShopify ? [] : [{ field: 'location_code', headerName: t('dataHealth.colCode'), width: 120 }]),
        { field: 'location_name', headerName: t('dataHealth.colLocation'), ...flexColumn(1, 160) },
      ];
      if (def.actionType === 'erp-leftover') {
        cols.push({ field: 'on_hand_total', headerName: t('dataHealth.colOnHand'), width: 110, type: 'numberColumn' });
        cols.push(actionColumn({
          colId: 'erp_leftover_actions', headerName: t('dataHealth.colAction'),
          cellRenderer: ErpLeftoverActionsCellRenderer,
        }));
      }
      return cols;
    }

    // --- Item-based columns ---
    // Identity comes from the one seam every item grid uses (productIdentity):
    // a Product cell (image + name + SKU) on Shopify, item_code + name on ERP
    // stores, where item_code is a value the merchant knows. Like the reports,
    // the ERP shape carries no standalone SKU column — the widest card here
    // (price_below_cost, four price columns) only fits the 700px grid without
    // it — except on duplicate_sku, the one issue about an existing SKU value,
    // which the merchant must see to tell the two rows apart.
    const cols = itemIdentityColumns(isShopify, {
      productHeader: t('dataHealth.colProduct'),
      product: { valueGetter: itemValueGetter, skuField: 'sku', ...flexColumn(1, 220) },
      codeHeader: t('dataHealth.colItemCode'),
      code: { width: 120 },
      skuHeader: issueKey === 'duplicate_sku' ? t('dataHealth.colSku') : undefined,
      sku: issueKey === 'duplicate_sku' ? { width: 120 } : undefined,
      nameHeader: t('dataHealth.colItem'),
      name: { valueGetter: itemValueGetter, ...flexColumn(1, 200) },
    });

    if (entity === 'item_location' || entity === 'inventory') {
      cols.push({ field: 'location_name', headerName: t('dataHealth.colLocation'), width: 160, valueFormatter: (params) => params.value || '-' });
    }

    switch (def.actionType) {
      case 'toggle':
        cols.push(actionColumn({
          field: 'toggle', headerName: t('dataHealth.colAction'),
          cellRenderer: ToggleFixButtonCellRenderer,
          cellRendererParams: { field: issueKey },
        }));
        break;
      case 'delete-row':
        cols.push(actionColumn({
          field: 'remove', headerName: t('dataHealth.colAction'),
          cellRenderer: RemoveButtonCellRenderer,
        }));
        break;
      case 'order-constraints-fix':
        cols.push(actionColumn({
          colId: 'fix_order_rules', headerName: t('dataHealth.colAction'),
          cellRenderer: FixOrderRulesButtonCellRenderer,
        }));
        break;
      case 'dos-target-fix': {
        // The two numbers the card compares, so the row says how short the
        // target is; the fix button names the setting to change.
        cols.push({ field: 'target_days_of_supply', headerName: t('dataHealth.colDaysOfSupplyTarget'), width: 150, type: 'numberColumn' });
        cols.push({ field: 'cycle_days', headerName: t('dataHealth.colDaysBetweenReviews'), width: 150, type: 'numberColumn' });
        cols.push(actionColumn({
          colId: 'fix_dos_target', headerName: t('dataHealth.colAction'),
          cellRenderer: FixDosTargetCellRenderer,
        }));
        break;
      }
      case 'erp-leftover':
        cols.push({ field: 'on_hand_total', headerName: t('dataHealth.colOnHand'), width: 110, type: 'numberColumn' });
        cols.push(actionColumn({
          colId: 'erp_leftover_actions', headerName: t('dataHealth.colAction'),
          cellRenderer: ErpLeftoverActionsCellRenderer,
        }));
        break;
      case 'create-in-channel':
        cols.push(actionColumn({
          colId: 'create_in_channel', headerName: t('dataHealth.colAction'),
          // Bind the cell VALUE to the created-state. The row deliberately stays
          // in the list after a successful create (to show the "Finish setup"
          // link), so this cell must switch from the button to the link in
          // place. AG Grid only re-renders a cell when its value changes on an
          // immutable-data update; with no value binding the action column
          // would keep showing "Create", and a second click would fail on the
          // backend because the product now exists.
          valueGetter: (p) => (p.data?.channel_created ? 'created' : 'create'),
          cellRenderer: CreateInChannelButtonCellRenderer,
        }));
        break;
      case 'category-edit': {
        // Current category column (read-only context); typically '—' for
        // missing_product_category since the whole filter is "is null". Its
        // value is the label of the node the item points at
        // (`product_category_label` in api/views/data_health.py), which on a
        // product-type store is the node derived from the type — its
        // display_label IS the type
        // (api/services/catalog_derivations.py:_upsert_product_type_nodes).
        cols.push({
          field: 'product_category_label',
          headerName: t('dataHealth.colCurrentCategory'),
          width: 200,
          valueFormatter: ({ value }) => value || '—',
        });
        // No capability yet (auth bootstrap, before a store is selected) means
        // we do not know WHICH field fixes these rows. The honest grid is the
        // one above without a fix cell — an editor built on a guess is how a
        // merchant gets rows that can only fail.
        if (!categoryEditing) break;
        // Pick column — ONE cell for both stores (utils/categoryPickerColDef).
        // The store's capability decides what it offers: the Shopify taxonomy,
        // or the store's own product types plus a create row for one it has
        // never used. The header follows the same capability, because the
        // merchant is naming a product type on one store and a category on the
        // other, and a column called the wrong thing is a column they cannot
        // check.
        //
        // On a channel-connected (Shopify) store the write pushes a
        // productUpdate — an item with no channel product can't be given one
        // here, so the cell is display-only for those rows (same pattern as the
        // cost section's supplier gate); they're fixed under
        // erp_item_missing_channel_product. On non-channel stores the write is
        // local, so it stays editable.
        const picksProductType = writesCategory(categoryEditing, 'item_type');
        cols.push(entryCol({
          ...categoryPickerColDef({
            capability: categoryEditing,
            field: CATEGORY_FIX_FIELD,
            queryClient,
            storeId: selectedStore?.id,
            placeholder: picksProductType
              ? t('productType.searchPlaceholder')
              : t('dataHealth.categorySearchPlaceholder'),
            createLabel: (name) => t('productType.createOption', { name }),
          }),
          headerName: picksProductType
            ? t('dataHealth.colNewProductType')
            : t('dataHealth.colNewCategory'),
          width: picksProductType ? 240 : 260,
          // Gate on shopify_product_gid (already on the row) — same "has a channel
          // product" signal ItemSetup uses, so the two grids agree on one field.
          editable: (params) => !(isShopify && !params.data?.shopify_product_gid),
          // Overrides the builder's renderer for the no-channel guard. The pick
          // is read off the row (the taxonomy cell's valueFormatter emits the
          // id for the clipboard round-trip, not the label) and labelled by
          // `categoryCellLabel` — the one place that knows a cell value is an
          // option object on one store and a plain string on the other.
          cellRenderer: (params) => {
            if (isShopify && !params.data?.shopify_product_gid) {
              return <span style={{ color: 'var(--color-text-muted)' }}>{t('dataHealth.categoryNoChannelProduct', { channel })}</span>;
            }
            return categoryCellLabel(categoryEditing, params.data?.[CATEGORY_FIX_FIELD]) || '—';
          },
        }));
        break;
      }
      case 'assign-location':
        // Gated on the shared rule, exactly as missing_supplier is below: the
        // picker only when the list arrived non-empty, and an alert naming
        // which of the other two states we're in otherwise.
        if (optionsState === FIX_OPTIONS_STATE.READY) {
          cols.push(dropdownEditCol({
            field: '_new_location',
            headerName: t('dataHealth.missing_locationColHeader'),
            width: 220,
          }));
        }
        break;
      case 'dropdown-edit':
        // missing_supplier: per-row picker (supplierAssignPickerCol) — assigns a
        // store supplier on an item with none, or elects one of the item's
        // assigned suppliers when it has some but no default (ERP). Gated on the
        // store having ANY supplier; the empty case shows the "go to Supplier
        // Setup" alert instead of an empty picker.
        if (issueKey === 'missing_supplier') {
          if (hasSuppliers) cols.push(supplierAssignPickerCol());
        } else if (optionsState === FIX_OPTIONS_STATE.READY) {
          cols.push(dropdownEditCol({
            field: '_new_group',
            headerName: t(`dataHealth.${issueKey}ColHeader`),
          }));
        }
        break;
      case 'price-edit': {
        const isCost = def.fieldName === 'current_cost_price_supplier_ccy';
        if (isCost) {
          // User is entering a new supplier-ccy cost. Reference column is
          // the store-ccy sell price.
          cols.push({
            field: 'current_sell_price',
            headerName: t('dataHealth.colSellPrice'),
            width: 160,
            type: 'decimalColumn',
            valueFormatter: ({ value, data }) =>
              value != null ? formatCurrency(value, data?.currency_code) : '',
          });
        } else {
          // User is entering a new store-ccy sell price. Reference column
          // is the supplier-ccy cost.
          cols.push({
            field: 'current_cost_price_supplier_ccy',
            headerName: t('dataHealth.colCostPrice'),
            width: 160,
            type: 'decimalColumn',
            valueFormatter: ({ value, data }) =>
              value != null ? formatCurrency(value, data?.cost_currency_code) : '',
          });
        }
        cols.push(entryCol({
          field: '_new_price',
          headerName: isCost ? t('dataHealth.colNewCost') : t('dataHealth.colNewSell'),
          width: 160,
          type: 'decimalColumn',
          valueParser: priceParser,
          valueFormatter: ({ value, data }) => {
            if (value == null) return '';
            const ccy = isCost ? data?.cost_currency_code : data?.currency_code;
            return formatCurrency(value, ccy);
          },
        }));
        break;
      }
      case 'text-edit':
        // Widest of the entry columns: a SKU is a free-text identifier the
        // merchant types in full, and (unlike every other item issue) this grid
        // has no reference column beside it — just the identity and this one.
        cols.push(entryCol({
          field: '_new_sku',
          headerName: t('dataHealth.colSku'),
          width: 260,
        }));
        break;
      case 'quantity-edit':
        cols.push(
          {
            field: 'inventory_on_hand',
            headerName: t('dataHealth.colOnHand'),
            width: 80,
            type: 'numberColumn',
            valueFormatter: ({ value }) => value != null ? formatNumber(value, 0) : '',
          },
          entryCol({
            field: '_new_quantity',
            headerName: t('dataHealth.colNewQty'),
            width: ENTRY_COL_MIN_WIDTH,
            type: 'numberColumn',
            valueParser: (params) => {
              if (params.newValue === '' || params.newValue === null) return null;
              const parsed = Math.round(parseNumber(params.newValue));
              if (isNaN(parsed)) return params.oldValue;
              return Math.max(0, Math.min(10_000_000, parsed));
            },
            valueFormatter: ({ value }) => value != null ? formatNumber(value, 0) : '',
          }),
        );
        break;
      case 'activate':
        cols.push(
          {
            field: 'on_hand_total',
            headerName: t('dataHealth.colOnHand'),
            width: 80,
            type: 'numberColumn',
            valueFormatter: ({ value }) => value != null ? formatNumber(value, 0) : '',
          },
          actionColumn({ field: 'activate', headerName: t('dataHealth.colAction'), cellRenderer: ActivateButtonCellRenderer }),
        );
        break;
      case 'price-below-cost':
        cols.push(
          {
            field: 'current_cost_price_supplier_ccy',
            headerName: t('dataHealth.colCostPrice'),
            width: 160,
            type: 'decimalColumn',
            valueFormatter: ({ value, data }) =>
              value != null ? formatCurrency(value, data?.cost_currency_code) : '',
          },
          entryCol({
            field: '_new_cost',
            headerName: t('dataHealth.colNewCost'),
            width: 160,
            type: 'decimalColumn',
            valueParser: priceParser,
            valueFormatter: ({ value, data }) =>
              value != null ? formatCurrency(value, data?.cost_currency_code) : '',
          }),
          {
            field: 'current_sell_price',
            headerName: t('dataHealth.colSellPrice'),
            width: 160,
            type: 'decimalColumn',
            valueFormatter: ({ value, data }) =>
              value != null ? formatCurrency(value, data?.currency_code) : '',
          },
          entryCol({
            field: '_new_sell',
            headerName: t('dataHealth.colNewSell'),
            width: 160,
            type: 'decimalColumn',
            valueParser: priceParser,
            valueFormatter: ({ value, data }) =>
              value != null ? formatCurrency(value, data?.currency_code) : '',
          }),
        );
        break;
      // external / no actionType: no action column
    }

    return cols;
  }, [issueKey, entity, t, formatNumber, formatCurrency, dropdownOptions, optionsState, hasSuppliers, def, parseNumber, queryClient, categoryEditing, selectedStore?.id, isShopify, channel]);

  const isBatchEditable = BATCH_EDITABLE_TYPES.has(def?.actionType);


  const handleSaveAll = useCallback(async () => {
    if (!def || !isDirty || !def.viewset) return;


    // Extract each changed row's new value(s) into the shared value-bag shape;
    // `buildItem` owns the field/coercion/dual/category semantics AND the
    // create-vs-patch wire shape (assign-location posts to bulk-create). The
    // bag's slot ↔ `_new_*` mapping below is this grid's input-store detail
    // (its cell editors stash edits on the row as `_new_*`), which is why it
    // stays here rather than in the shared module.
    const readBag = (row) => {
      if (def.actionType === 'assign-location') return { locationId: row._new_location };
      if (def.actionType === 'price-below-cost') return { cost: row._new_cost, sell: row._new_sell };
      if (def.actionType === 'category-edit') return { category: row[CATEGORY_FIX_FIELD] };
      const value =
        def.actionType === 'text-edit' ? row._new_sku
        : def.actionType === 'quantity-edit' ? row._new_quantity
        : def.actionType === 'dropdown-edit'
          ? (issueKey === 'missing_supplier' ? row._new_supplier : row._new_group)
        : row._new_price;
      return { value };
    };

    const items = changedRows
      .map(row => buildItem(issueKey, row.id, readBag(row), { categoryEditing }))
      .filter(Boolean);

    if (items.length === 0) return;

    // Transport: normalize apiJson's throw-on-non-2xx into the {status, body}
    // shape resolveIssue negotiates gates on. A 409 PO gate and a 200 currency
    // gate are protocol, not failure — surface them; re-raise anything else.
    const transport = {
      post: async (endpoint, payload) => {
        try {
          const body = await actionMutation.mutateAsync({ endpoint, payload });
          return { status: 200, body };
        } catch (err) {
          if (err instanceof ApiError) return { status: err.status, body: err.body };
          throw err;
        }
      },
    };

    // Confirm: this app renders both gates in MUI — <ConfirmDialog> for the PO
    // impact, the full CurrencyChangeConfirmModal (via useResolutionGate) for
    // the cost-of-record adoption. Both hooks' request already resolves true on
    // confirm / false on cancel — exactly the boolean contract.
    const confirm = {
      poDeletion: ({ count }) => poGate.confirm({
        title: t('dataHealth.confirmPoDeletionTitle'),
        message: t('dataHealth.confirmPoDeletion', { count }),
        confirmLabel: t('dataHealth.confirmPoDeletionButton'),
        variant: 'warning',
      }),
      currencyChange: ({ changeContext, affectedItems, retry }) =>
        currencyGate.request({
          changeContext,
          affectedItems,
          onConfirm: async (strategy) => {
            const r = await retry(strategy);
            // Three distinct outcomes. "Still asking" is not a failed save —
            // nothing was written and nothing broke — so it gets its own
            // sentence rather than sending the merchant to look for damage.
            if (r.requiresConfirmationAgain) return { error: t('dataHealth.confirmationAskedAgain') };
            if (r.error) return { error: translateRequestError(r.error, t, 'dataHealth.updateFailed') };
            // ok → return nothing to close the modal
          },
        }),
    };

    let outcome;
    try {
      outcome = await resolveIssue({ issueKey, items, transport, confirm });
    } catch (err) {
      console.error('DataHealth batch save failed:', err.message);
      showToast(translateRequestError(err, t, 'dataHealth.updateFailed'), 'error');
      return;
    }

    if (outcome.cancelled) return;
    if (outcome.failedRows.length > 0) {
      showToast(failedRowsMessage(t, outcome.failedRows), 'error');
    }

    // Clearing the dirty markers is this grid's own business — `successIds`
    // means the write landed. What LEAVES the grid, what the new total is, and
    // whether a re-read is owed are datahealth/reconcile.js's rule: the server
    // names the resolved rows for every issue, so there is one path for all of
    // them.
    clearByIds(outcome.successIds);

    const { remaining, removed, totalItems, needsReread } = reconcileRows(rowData, outcome, {
      totalItems: pagination?.total_items ?? rowData.length,
    });

    // Only touch the buffer when rows actually left it. `remaining` is a fresh
    // array every time, so setting it unconditionally would hand AG Grid a new
    // row set — and re-render every row — after a save where nothing resolved
    // (every row failed, say).
    if (removed > 0) {
      setRowData(remaining);
      setPagination(prev => prev ? { ...prev, total_items: totalItems } : prev);
      onItemFixed?.(removed);
    }
    // `fetchWindow(null)` is this grid's re-read: stale row values after a write
    // that resolved nothing, or a buffer emptied while the server still holds
    // rows.
    if (needsReread) fetchWindow(null);
  }, [issueKey, def, categoryEditing, changedRows, rowData, pagination, onItemFixed, fetchWindow, t, actionMutation, clearByIds, isDirty, currencyGate, poGate]);

  if (loading) {
    return (
      <div className="grid-loading-center">
        <CircularProgress size={24} />
      </div>
    );
  }

  if (error) return <PageBanner severity="error" messages={[error]} />;
  if (!rowData || rowData.length === 0) return <PageBanner severity="info" messages={[t('dataHealth.noIssues')]} />;

  // A count for the button's label. Whether there IS more to fetch is the
  // server's cursor, not this subtraction.
  const remaining = pagination ? pagination.total_items - rowData.length : 0;

  return (
    <Stack spacing={1}>
      {def?.actionType === 'category-edit' && <ProductCategoryPickerWarmer />}

      {/* A picker-fix card whose picker is withheld says which of the two
          states it is in. Neither may be silent: a card that lists broken rows
          and offers no affordance, with no explanation, is a dead end the
          merchant can't diagnose — and the two need different sentences,
          because only one of them has a next step. */}
      <PageBanner
        severity="error"
        messages={[issueKey === 'missing_location' && optionsState === FIX_OPTIONS_STATE.ERROR
          && t('dataHealth.locationOptionsError')]}
      />
      {issueKey === 'missing_location' && optionsState === FIX_OPTIONS_STATE.EMPTY && (
        <PageBanner
          severity="warning"
          messages={[t('dataHealth.noLocationsDesc')]}
          actions={[{ label: t('dataHealth.noLocationsAction'), onClick: () => openTab('/locations-setup') }]}
        />
      )}
      <PageBanner
        severity="error"
        messages={[issueKey === 'missing_supplier' && suppliersState === FIX_OPTIONS_STATE.ERROR
          && t('dataHealth.supplierCountError')]}
      />
      {issueKey === 'missing_supplier' && suppliersState === FIX_OPTIONS_STATE.EMPTY && (
        <PageBanner
          severity="warning"
          messages={[t('dataHealth.noSuppliersDesc')]}
          actions={[{ label: t('dataHealth.noSuppliersAction'), onClick: () => openTab('/supplier-setup') }]}
        />
      )}

      <div className="data-health-grid">
        <AgGridReact
          {...defaultGridProps}
          {...(isBatchEditable ? editableGridProps : {})}
          ref={gridRef}
          theme={gridTheme}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          columnTypes={columnTypes}
          getRowId={getRowId}
          domLayout="autoHeight"
          sideBar={false}
          {...gridState}
          context={gridContext}
          onCellValueChanged={isBatchEditable ? onCellValueChanged : undefined}
          // Grid is a fixed 700px wide (CSS), centered in the card. The name
          // column of the issue's entity (supplier / location / product) flexes
          // to fill it; everything else — the action column above all — is
          // measured to its content. The entry columns sit out auto-sizing
          // (see entryCol): they hold no content to measure, so measuring them
          // only ever shrank them.
          getContextMenuItems={copyOnlyContextMenuItems}
        />
      </div>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        {isBatchEditable && isDirty && (
          <Button
            variant="contained"
            onClick={handleSaveAll}
            loading={batchSaving}
          >
            {t('dataHealth.saveCount', { count: changedRows.length })}
          </Button>
        )}

        {pagination?.next_cursor && (
          <Button
            variant="outlined"
            onClick={() => fetchWindow(pagination.next_cursor)}
            loading={loadingMore}
          >
            {t('dataHealth.showMore', { count: remaining })}
          </Button>
        )}
      </Box>

      <CurrencyChangeConfirmModal {...currencyGate.modalProps} />
      <ConfirmDialog {...poGate.dialogProps} />
      <ConfirmDialog {...discardGate.dialogProps} />

      {linkSupplier && activeErp && (
        <LinkSupplierToExactDialog
          open
          connectorId={activeErp.id}
          supplier={{ supplier_id: linkSupplier.id, supplier_name: linkSupplier.supplier_name }}
          onClose={() => setLinkSupplier(null)}
          onLinked={() => {
            const id = linkSupplier.id;
            setLinkSupplier(null);
            // The supplier now carries an erp_id, so it no longer matches the
            // issue — drop it and decrement the count.
            setRowData(prev => prev.filter(r => r.id !== id));
            setPagination(prev => prev ? { ...prev, total_items: prev.total_items - 1 } : prev);
            onItemFixed?.();
          }}
        />
      )}
    </Stack>
  );
}


// ============================================
// TaskCard — issue row with progressive disclosure
// ============================================

// How long a switched card stays where it is, at least, before it leaves for
// its new section — long enough to see the switch move and the card gray out.
const SWITCH_HOLD_MS = 400;
// The leave itself: the card fades out while its height closes.
const LEAVE_MS = 300;

/**
 * `ignored` is the state the card shows. `move` is set while the include
 * switch is carrying the card to the other section: 'switching' while it waits
 * in place showing the new state, 'leaving' while it fades out; `onMoved` is
 * called once it is gone.
 */
function TaskCard({ issueKey, entity, computed, count, bulkRemedy, currentlyNegative, expanded, onToggle, onItemFixed, onUnsavedEdits, ignored, onSetIgnored, move, onMoved }) {
  const { t } = useTranslation();
  const { openTab } = useTabs();
  const { selectedStore } = useAuth();
  const channel = channelName(selectedStore?.store_type, t);
  const def = issueDef(issueKey);
  // The note names the field the merchant is about to edit, and on a
  // product-type store that field is the product type — which is
  // PRODUCT-scoped, so the note also has to say the edit reaches every variant.
  // Both facts come off the capability (`scope`), not off a second reading of
  // the store's mode. Written as literal keys, not a dynamic pattern for
  // audit-i18n to pin.
  const categoryEditing = useCategoryEditing();
  const infoNote = issueKey === 'missing_product_category'
      && categoryEditing?.scope === 'product'
    ? t('dataHealth.missing_product_categoryInfoNote_productType')
    : t(`dataHealth.${issueKey}InfoNote`, '');

  const IconComponent = ICON_MAP[def.icon] || WarningCircle;
  const iconColor = ignored ? 'text.disabled' : (TONE_COLORS[def.tone] || 'text.secondary');

  // Count-chip noun follows the backend-declared entity (single source of
  // truth on the summary) — item_location / inventory both read as "items".
  // No per-key branch: duplicate_items declares entity='pair'.
  const countLabel = entity === 'pair'
    ? `${count} ${count === 1 ? t('dataHealth.duplicates.pair') : t('dataHealth.duplicates.pairs')}`
    : entity === 'supplier'
    ? `${count} ${count === 1 ? t('dataHealth.supplier') : t('dataHealth.suppliers')}`
    : entity === 'location'
    ? `${count} ${count === 1 ? t('dataHealth.location') : t('dataHealth.locations')}`
    : (entity === 'item_location' || entity === 'inventory')
    ? `${count} ${count === 1 ? t('dataHealth.productLocation') : t('dataHealth.productLocations')}`
    : `${count} ${count === 1 ? t('dataHealth.product') : t('dataHealth.products')}`;

  const chipColor = def.tone === 'critical' ? 'error' : def.tone === 'warning' ? 'warning' : 'neutral';

  // Closing the card — collapsing it, or ignoring its issue — unmounts its
  // grid, so the grid's unsaved edits are asked about first. A card with no
  // grid mounted (collapsed, computed) has nothing to ask.
  const issueGridRef = useRef(null);
  const closeGrid = async () => !issueGridRef.current || issueGridRef.current.release();
  const toggle = async () => {
    if (expanded && !(await closeGrid())) return;
    onToggle();
  };
  // The switch sets the opposite of what the card shows, as a state: a card
  // kept open over unsaved edits may show an issue another tab excluded.
  const toggleIgnored = async () => {
    if (move) return;
    if (!(await closeGrid())) return;
    onSetIgnored(issueKey, !ignored);
  };
  // Excluded cards do not open — their grid is gated off below — and neither
  // does a card on its way to the other section.
  const opens = !ignored && !move;

  // An excluded card fades everything but its switch, which is the way back
  // and must not look disabled.
  const fade = { opacity: ignored ? 0.5 : 1, transition: 'opacity var(--transition-base)' };
  const title = (
    <Typography component="span" variant="body2" fontWeight="var(--font-weight-semibold)" color={ignored ? 'text.disabled' : 'text.primary'}>
      {t(`dataHealth.${issueKey}Title`, { channel })}
    </Typography>
  );

  return (
    <Collapse in={move !== 'leaving'} timeout={LEAVE_MS} onExited={() => onMoved(issueKey)}>
      <Paper
        variant="outlined"
        sx={{ overflow: 'hidden', opacity: move === 'leaving' ? 0 : 1, transition: `opacity ${LEAVE_MS}ms` }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5,
            px: 2, py: 1.5,
          }}
        >
          {/* Icon */}
          <Box sx={{ ...fade, color: iconColor, flexShrink: 0, display: 'flex' }}>
            <IconComponent size={ICON.xl} weight="fill" />
          </Box>

          {/* Title + chips, benefit below */}
          <Box sx={{ ...fade, flex: '1 1 16rem', minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              {/* The title is the card's open/close button. Its ::after
                  stretches over the whole header — `position: static` hands the
                  anchor from ButtonBase (relative by default) to the header — so
                  a click anywhere on the row opens the card, while the switch,
                  layered above it, stays a separate control rather than one
                  nested inside. */}
              {!opens ? title : (
                <ButtonBase
                  disableRipple
                  onClick={toggle}
                  aria-expanded={expanded}
                  sx={{
                    position: 'static',
                    justifyContent: 'flex-start', textAlign: 'left',
                    '&::after': { content: '""', position: 'absolute', inset: 0 },
                    // The app's focus ring (base.css :focus-visible), moved
                    // from the title onto the header it stands for; inset,
                    // because the card's Paper clips an outset ring.
                    '&:focus-visible': { outline: 'none' },
                    '&:focus-visible::after': {
                      outline: '2px solid var(--color-link)', outlineOffset: '-2px',
                    },
                  }}
                >
                  {title}
                </ButtonBase>
              )}
              {/* Count chip */}
              <StatusChip label={countLabel} color={ignored ? 'neutral' : chipColor} />
              {/* Overselling only: how many of these items are below zero right
                  now. The negative-inventory card skips them on purpose (selling
                  past zero is what the merchant configured), so this is the one
                  place the standing backorders are named. Same tone as the card —
                  a backorder is normal, not a fault — and absent at zero. */}
              {currentlyNegative > 0 && (
                <StatusChip
                  label={t('dataHealth.oversellingBelowZero', { count: currentlyNegative })}
                  color="neutral"
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {t(`dataHealth.${issueKey}Benefit`, { channel })}
            </Typography>
          </Box>

          <FormControlLabel
            control={<Switch size="small" checked={!ignored} onChange={toggleIgnored} />}
            label={t('dataHealth.includeInChecks')}
            sx={{ position: 'relative', zIndex: 1, m: 0, ml: 'auto', flexShrink: 0, whiteSpace: 'nowrap' }}
          />

          {opens && (
            <DisclosureCaret open={expanded} size={ICON.xl} style={{ color: 'var(--color-text-muted)' }} />
          )}
        </Box>

        <Collapse in={expanded}>
          {expanded && (
            <Stack spacing={1.5} sx={{ px: 2, pb: 2 }}>
              <PageBanner severity="info" messages={[infoNote]} />
              {/* The store-level shortcut, above the row grid. The grid is the
                  long way round — fixing thousands of rows one at a time — and
                  a merchant who never scrolls past it never learns the bulk
                  import exists. Not shown on a paused card: the merchant chose
                  to silence this issue. */}
              {!ignored && bulkRemedy?.kind === 'shopify_cost_import' && (
                <PageBanner
                  severity="info"
                  messages={[t('dataHealth.bulkRemedy.shopifyCostImport', { count: bulkRemedy.eligible })]}
                  actions={[{
                    label: t('dataHealth.bulkRemedy.shopifyCostImportAction'),
                    onClick: () => openTab('/cost-import'),
                  }]}
                />
              )}
              {/* `computed` is the BACKEND's word (IssueSpec.computed, sent on the
                  summary): it counted this issue from an engine, so there is no
                  row population and /api/data-health/detail/ answers 400. Gating
                  the grid on it — rather than on the frontend's own actionType —
                  is what stops a new computed issue mounting a grid that can only
                  error. Those cards resolve elsewhere: `duplicate-review` owns its
                  bespoke pair UI, `page-link` opens the screen that owns the fix. */}
              {!ignored && (computed
                ? (def.actionType === 'duplicate-review'
                  ? <DuplicateReview onItemFixed={onItemFixed} />
                  : def.path && (
                    <Button variant="contained" sx={{ alignSelf: 'flex-start' }} onClick={() => openTab(def.path)}>
                      {t(`dataHealth.${issueKey}Cta`)}
                    </Button>
                  ))
                : <IssueGrid ref={issueGridRef} issueKey={issueKey} entity={entity} onItemFixed={onItemFixed} onUnsavedEdits={onUnsavedEdits} />)}
            </Stack>
          )}
        </Collapse>
      </Paper>
    </Collapse>
  );
}


// ============================================
// CompletedSection
// ============================================

function CompletedSection({ completedKeys, allClear }) {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const channel = channelName(selectedStore?.store_type, t);
  const [expanded, setExpanded] = useState(allClear);

  if (completedKeys.length === 0) return null;

  return (
    <Tile>
      <ButtonBase
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}
      >
        <CheckCircle size={ICON.xl} style={{ color: 'var(--color-success)' }} />
        <Typography component="span" variant="body2" color="text.secondary">
          {t('dataHealth.completedCount', { count: completedKeys.length })}
        </Typography>
        <DisclosureCaret open={expanded} size={ICON.md} style={{ color: 'var(--color-text-muted)' }} />
      </ButtonBase>

      <Collapse in={expanded}>
        <List dense disablePadding sx={{ pl: 4.5 }}>
          {completedKeys.map(key => (
            <ListItem key={key} disableGutters sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <CheckCircle size={ICON.md} style={{ color: 'var(--color-success)' }} />
              </ListItemIcon>
              <ListItemText
                primary={t(`dataHealth.${key}Done`, { channel })}
                slotProps={{ primary: { variant: 'body2', color: 'text.secondary' } }}
              />
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Tile>
  );
}


// ============================================
// Main DataHealth page
// ============================================

export default function DataHealth() {
  const { t, i18n } = useTranslation();
  const { openTab } = useTabs();
  const queryClient = useQueryClient();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const channel = channelName(selectedStore?.store_type, t);
  const setIgnoredMutation = useSetDataHealthIgnored();
  // Connector-health issues (ERP orphans / links) only apply when an ERP is
  // connected. Without one their count is 0 because they're N/A, not because
  // they were fixed — so they must stay out of the "completed" list.
  const { connector: erpConnector, error: connectorsError } = useActiveErp();
  const erpManaged = erpConnector !== null;

  // Summary state lives in the TanStack cache. Shape: { issues[],
  // category_order, needs_attention_count, ignored_issues, ... } — the
  // registry-driven payload the page groups into sections below.
  const { data: summary, loading, error: fetchError, refetch: refetchSummary } = useDataHealthSummary();
  const [expandedIssues, setExpandedIssues] = useState(new Set());
  // The issues whose card holds unsaved edits. Such a card stays in the list
  // whatever a summary reload reads — a save on another card, a return to the
  // browser tab or an import's end can read its issue at 0 or excluded — so
  // the grid holding the edits is never unmounted under them; it leaves once
  // they are saved or discarded.
  const [issuesWithEdits, setIssuesWithEdits] = useState(() => new Set());
  const reportUnsavedEdits = useCallback((issueKey, hasEdits) => {
    setIssuesWithEdits((prev) => {
      if (prev.has(issueKey) === hasEdits) return prev;
      const next = new Set(prev);
      if (hasEdits) next.add(issueKey);
      else next.delete(issueKey);
      return next;
    });
  }, []);
  // The cards the include switch is carrying to the other section, by issue:
  // `{ ignored, phase }` — the state switched to, and the TaskCard `move` it
  // is in. Such a card stays in the section it was switched in, showing the
  // new state, until it has faded out — whatever the summary already says of
  // its issue.
  const [movingIssues, setMovingIssues] = useState(() => new Map());
  const setMove = useCallback((issueKey, move) => {
    setMovingIssues((prev) => {
      const next = new Map(prev);
      if (move) next.set(issueKey, move);
      else next.delete(issueKey);
      return next;
    });
  }, []);
  const handleMoved = useCallback((issueKey) => setMove(issueKey, null), [setMove]);
  const refreshTimerRef = useRef(null);

  // Debounced refresh — coalesces rapid inline fixes (multiple chip
  // updates from a single bulk edit) into a single re-fetch.
  const scheduleSummaryRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => refetchSummary(), 1500);
  }, [refetchSummary]);

  // Tear down the pending debounce on unmount so the timer doesn't
  // fire after the page is gone.
  useEffect(() => {
    return () => { if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current); };
  }, []);

  const handleItemFixed = useCallback((issueKey, count = 1) => {
    // Optimistic update — decrement the chip count immediately in the
    // TanStack-cached summary, preserving its shape. Whether this fix emptied
    // the issue is read before the write: an updater must stay pure. The page
    // renders from this query, so the cache holds it while a card is open.
    const key = dataHealthSummaryKey.all(storeId);
    const before = queryClient.getQueryData(key).issues.find((issue) => issue.key === issueKey);
    queryClient.setQueryData(key, (prev) => ({
      ...prev,
      issues: prev.issues.map((issue) => (
        issue.key === issueKey ? { ...issue, count: Math.max(0, issue.count - count) } : issue
      )),
    }));
    if (before.count > 0 && before.count - count <= 0) {
      if (issueKey === 'missing_supplier') {
        showToast(t('dataHealth.supplierCompleteMessage'), 'success', {
          label: t('nav.createPurchaseOrders'),
          onClick: () => openTab('/purchase-recommendations'),
        });
      } else {
        showToast(t('dataHealth.issueDone', { title: t(`dataHealth.${issueKey}Title`, { channel }) }));
      }
    }
    // Re-fetch summary to get accurate products_with_issues and cross-issue counts
    scheduleSummaryRefresh();
    // The bell-icon badge refreshes on its own: the fix mutation
    // invalidates notifications.badge, and the (always-mounted) bell
    // query refetches immediately.
  }, [queryClient, storeId, t, scheduleSummaryRefresh, channel, openTab]);

  const handleToggleIssue = useCallback((issueKey) => {
    setExpandedIssues(prev => {
      const next = new Set(prev);
      if (next.has(issueKey)) next.delete(issueKey);
      else next.add(issueKey);
      return next;
    });
  }, []);

  const handleSetIgnored = useCallback(async (issueKey, ignored) => {
    if (ignored) {
      setExpandedIssues(prev => {
        const next = new Set(prev);
        next.delete(issueKey);
        return next;
      });
    }
    setMove(issueKey, { ignored, phase: 'switching' });
    const hold = new Promise((resolve) => { setTimeout(resolve, SWITCH_HOLD_MS); });
    try {
      // Resolves once the summary listing the issue in its new section is in.
      await setIgnoredMutation.mutateAsync({ issue: issueKey, ignored });
      await hold;
      setMove(issueKey, { ignored, phase: 'leaving' });
    } catch (err) {
      setMove(issueKey, null);
      console.error('DataHealth exclude/include failed:', err.message);
      showToast(translateRequestError(err, t, 'dataHealth.updateFailed'), 'error');
    }
  }, [t, setIgnoredMutation, setMove]);

  if (loading) {
    return (
      <div className="page-container">
        <Tile>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 6 }}>
            <CircularProgress />
            <Typography color="text.secondary">{t('dataHealth.loading')}</Typography>
          </Box>
        </Tile>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="page-container">
        <PageBanner severity="error" messages={[fetchError]} />
      </div>
    );
  }

  // Partition the registry-driven issue list (single source of truth) into the
  // page's three regions. "Needs attention" is the backend's count, from the
  // same rule as the bell badge, so the two always agree.
  const issues = summary.issues;
  const activeByCategory = new Map();   // category → issues to show (count>0, !ignored)
  // Every excluded issue, at any count. "Include in checks" on these cards is the
  // ONLY way back into the health score, so an excluded issue that drops to
  // count 0 must stay listed — filtering it out (or celebrating it as
  // "completed", which it isn't: nobody fixed it, it was opted out of)
  // strands the exclusion permanently.
  const pausedIssues = [];              // ignored, any count
  const completedKeys = [];             // resolved (count==0), shown as an affirmative state
  const needsAttention = summary.needs_attention_count;

  // A resolved issue is celebrated in the completed list only when it has an
  // affirmative completed-state string (`<key>Done`) — the curated set that reads
  // as an achievement (so deliberate-exclusion and transient issues are omitted) —
  // AND it actually applies to this store: connector-health (ERP) issues sit at
  // count 0 because they're N/A when no ERP is connected, not because they were
  // fixed, so they're excluded there.
  const canComplete = (issue) => (
    !issue.auto_resolved
    && i18n.exists(`dataHealth.${issue.key}Done`)
    && (issue.category !== 'connector_health' || erpManaged)
  );

  // A card with unsaved edits stays among the active ones whatever the
  // summary says of its issue — excluded from checks (in another browser tab)
  // as much as at count 0: the excluded section renders no grid. A card the
  // include switch is moving stays in the section it was switched in.
  issues.forEach((issue) => {
    const move = movingIssues.get(issue.key);
    const listedIgnored = move ? !move.ignored : issue.ignored;
    if (issuesWithEdits.has(issue.key) || (!listedIgnored && (move || issue.count > 0))) {
      if (!activeByCategory.has(issue.category)) activeByCategory.set(issue.category, []);
      activeByCategory.get(issue.category).push(issue);
    } else if (listedIgnored) {
      pausedIssues.push(issue);
    } else if (canComplete(issue)) {
      completedKeys.push(issue.key);
    }
  });

  // "All clear" must mean a populated catalog with no issues — NOT merely
  // "zero issues found", which is vacuously true for a store whose catalog
  // hasn't synced yet. An empty/mid-sync store (every count 0) gets a "no
  // products yet" message instead.
  const hasCatalog = (summary.total_active_items ?? 0) > 0;
  const allClear = needsAttention === 0 && hasCatalog
    && issuesWithEdits.size === 0 && movingIssues.size === 0;
  const emptyCatalog = needsAttention === 0 && !hasCatalog;

  // A card in the section `listedIgnored` names; while the include switch
  // moves it, it shows the state switched to.
  const renderCard = (issue, listedIgnored) => {
    const move = movingIssues.get(issue.key);
    return (
      <TaskCard
        key={issue.key}
        issueKey={issue.key}
        entity={issue.entity}
        computed={issue.computed}
        count={issue.count}
        bulkRemedy={issue.bulk_remedy}
        currentlyNegative={issue.currently_negative}
        expanded={expandedIssues.has(issue.key)}
        onToggle={() => handleToggleIssue(issue.key)}
        onItemFixed={(count) => handleItemFixed(issue.key, count)}
        onUnsavedEdits={reportUnsavedEdits}
        ignored={move ? move.ignored : listedIgnored}
        onSetIgnored={handleSetIgnored}
        move={move?.phase}
        onMoved={handleMoved}
      />
    );
  };

  return (
    <div className="page-container">
      <PageBanner severity="error" messages={[connectorsError]} />

      {allClear && (
        <Tile title={t('dataHealth.allClear')}>
          <Typography variant="body2" color="text.secondary">
            {t('dataHealth.allClearDesc')}
          </Typography>
        </Tile>
      )}

      {emptyCatalog && (
        <Tile title={t('dataHealth.noCatalogTitle')}>
          <Typography variant="body2" color="text.secondary">
            {t('dataHealth.noCatalogDesc')}
          </Typography>
        </Tile>
      )}


      {/* Category sections — order from summary.category_order */}
      {(summary.category_order || []).map((category) => {
        const categoryIssues = activeByCategory.get(category);
        if (!categoryIssues || categoryIssues.length === 0) return null;

        return (
          <Tile
            key={category}
            title={t(`dataHealth.${category}Title`)}
            subtitle={t(`dataHealth.${category}Desc`)}
          >
            <Stack spacing={1}>
              {categoryIssues.map((issue) => renderCard(issue, false))}
            </Stack>
          </Tile>
        );
      })}

      {/* Paused section */}
      {pausedIssues.length > 0 && (
        <Tile title={t('dataHealth.pausedTitle')} subtitle={t('dataHealth.pausedDesc')}>
          <Stack spacing={1}>
            {pausedIssues.map((issue) => renderCard(issue, true))}
          </Stack>
        </Tile>
      )}

      {/* Completed section — only meaningful with a catalog. On an empty/
          mid-sync store every issue reads count 0, so "N issues fixed" would
          be false reassurance. */}
      {hasCatalog && (
        <CompletedSection completedKeys={completedKeys} allClear={allClear} />
      )}
    </div>
  );
}
